import React from "react";
import { ActionPointData, ActionPointRelation, PublicActionPlanLink, PublicAmorgUser, rng, stringUtil } from "../../../amorg-common";
import { createActionPoint, deleteActionPoint, updateActionPoint } from "../../../api/actionPointClientApi";
import { Label } from "../../../designSystem/Label";
import { LoadingPageOverlay } from "../../../designSystem/LoadingPageOverlay";
import { Modal } from "../../../designSystem/Modal";
import { Button } from "../../../designSystem/button/Button";
import { DeleteConfirmationModal } from "../../../designSystem/compositeComponents/DeleteConfirmationButton";
import { Flex } from "../../../designSystem/compositeComponents/FlexHelpers";
import { UserSelectorPanel } from "../../../designSystem/compositeComponents/itemSelector/UserSelectorPanel";
import { ActionPointMode, ActionPointRow } from "./ActionPointRow";


type ActionPointListPanelProps = {
    mode:ActionPointMode,
    users?: PublicAmorgUser[],
    actionPoints: ActionPointData[],
    disabled?: boolean,
    linkDisplay?:{
        siteId?:string,
        actionPlanId:string,
        links:PublicActionPlanLink[]
    },
    onUpdated: () => void;
};
export function ActionPointListPanel (props: ActionPointListPanelProps): JSX.Element
{
    async function handleClickCreateActionPoint ()
    {
        const nameBase = 'Nyt handlepunkt';
        const nextDescription = stringUtil.generateNextSequenceValue(nameBase, props.actionPoints.map(ap => ap.description));
        const relation: ActionPointRelation = props.mode.type === 'agendaItem'
            ? { name: 'AgendaItem', agendaItemId: props.mode.agendaItemId }
            : { name: 'ActionPlanTopic', actionPlanTopicId: props.mode.topicId };

        const result = await createActionPoint(rng.getRandomId(), relation, nextDescription, null, null);

        if (result.isSuccess)
            props.onUpdated();
    }

    return (
        <section>
            <ActionPointList
                actionPoints={props.actionPoints}
                users={props.users}
                mode={props.mode}
                linkDisplay={props.linkDisplay}
                disabled={props.disabled}
                onUpdated={props.onUpdated}
            />
            <Flex justifyContent="space-between" alignItems="center" gap={8} style={{ marginLeft: '60px' }}>
                <Label>
                    {props.actionPoints.length === 0 &&
                        "Ingen handlepunkter oprettet"
                    }
                </Label>

                <Button variant="text" size="32px" text="Opret handlepunkt" icon="actions_plus" onClick={handleClickCreateActionPoint} />
            </Flex>
        </section>
    );
}



type ActionPointListProps = {
    actionPoints: ActionPointData[],
    mode: {
        type: 'actionPlanTopic',
        topicId: string;
    } | {
        type: 'agendaItem',
        agendaItemId: string;
    },
    users?: PublicAmorgUser[],
    disabled?: boolean,
    linkDisplay?:{
        siteId?:string,
        actionPlanId:string,
        links:PublicActionPlanLink[]
    },
    onUpdated: () => void;
};
export function ActionPointList (props: ActionPointListProps): JSX.Element
{
    const [loading, setLoading] = React.useState(false);
    const [deleteActionPointState, setDeleteActionPointState] = React.useState<{ actionPointId: string; } | null>(null);
    const [userSelectorState, setUserSelectorState] = React.useState<{ actionPointId: string; } | null>(null);


    async function handleUpdateActionPointUser (userId: string)
    {
        if (!userSelectorState) return;

        setLoading(true);
        const ap = props.actionPoints.find(a => a.id === userSelectorState.actionPointId);
        if(!ap) return;
        await updateActionPoint(ap.id, ap.description, userId === '' ? null : { type: 'internal', userId }, ap.deadline, ap.done);
        setLoading(false);
        setUserSelectorState(null);

        props.onUpdated();
    }

    async function handleDeleteActionPoint (actionPointId: string)
    {
        setLoading(true);
        await deleteActionPoint(actionPointId);
        setLoading(false);
        setDeleteActionPointState(null);

        props.onUpdated();
    }

    return (
        <section>

            <LoadingPageOverlay loading={loading} />

            <DeleteConfirmationModal
                showModal={deleteActionPointState !== null}
                confirmationHeading="Slet handlepunkt?"
                confirmationText='Er du sikker på du ønsker at slette dette handlepunkt?'
                requireKey={false}
                onConfirmDelete={() => { deleteActionPointState && handleDeleteActionPoint(deleteActionPointState.actionPointId); }}
                buttonText="Slet handlepunkt"
                onCancel={() => setDeleteActionPointState(null)}
            />

            <Modal
                showModal={userSelectorState !== null}
                title="Vælg bruger"
                onCancel={() => setUserSelectorState(null)}
                content={
                    <UserSelectorPanel
                        users={props.users || []}
                        showNone
                        onSelectionChanged={handleUpdateActionPointUser}
                    />
                }
            />

            <Flex flexDirection="column" gap={4}>
            {props.actionPoints.map((ap) =>
            {
                return (
                    <ActionPointRow key={ap.id}
                        actionPoint={ap}
                        mode={props.mode}
                        disabled={props.disabled || false}
                        linkDisplay={props.linkDisplay}
                        onUpdated={props.onUpdated}
                        onDelete={() => setDeleteActionPointState({ actionPointId: ap.id })}
                        onSelectUser={() => setUserSelectorState({ actionPointId: ap.id })}
                    />
                );
            }
            )}
            </Flex>
        </section>
    );
}